import Image from 'next/image'

import { AnimatedProgressBars } from './AnimatedProgressBars'

const contentPadding = 'px-6 md:px-12'

const PROGRESS_ITEMS_BY_LOCALE: Record<AppLocale, Array<{ label: string; value: number }>> = {
  en: [
    { label: 'Filtration Efficiency', value: 65 },
    { label: 'Production Capacity', value: 93 },
    { label: 'Global Distribution Reliability', value: 41 },
  ],
  fr: [
    { label: 'Efficacité de filtration', value: 65 },
    { label: 'Capacité de production', value: 93 },
    { label: 'Fiabilité de la distribution mondiale', value: 41 },
  ],
  ar: [
    { label: 'كفاءة الترشيح', value: 65 },
    { label: 'القدرة الإنتاجية', value: 93 },
    { label: 'موثوقية التوزيع العالمي', value: 41 },
  ],
}

const ABOUT_GALLIUM_TEXT: Record<
  AppLocale,
  {
    label: string
    heading: string
    paragraph1: string
    paragraph2: string
  }
> = {
  en: {
    label: 'ABOUT GALLIUM FILTERS',
    heading: 'ENGINEERING FILTRATION FOR THE FUTURE',
    paragraph1:
      'Gallium Filters has been at the forefront of filtration technology for over two decades, delivering advanced solutions for air, oil, fuel, water, and hydraulic systems across global industries.',
    paragraph2:
      'Through continuous research, precision engineering, and strict quality control, we have built a reputation for filtration systems that exceed industry standards. Our wide product portfolio enables us to serve different sectors with reliability, flexibility, and long service life.',
  },
  fr: {
    label: 'À PROPOS DE GALLIUM FILTERS',
    heading: 'INGÉNIERIE DE FILTRATION POUR L’AVENIR',
    paragraph1:
      "Gallium Filters est à l'avant-garde de la technologie de filtration depuis plus de deux décennies, en proposant des solutions avancées pour les systèmes d'air, d'huile, de carburant, d'eau et hydrauliques dans les industries du monde entier.",
    paragraph2:
      'Grâce à une recherche continue, une ingénierie de précision et un contrôle qualité rigoureux, nous avons bâti une réputation de systèmes de filtration qui dépassent les standards du secteur. Notre large portefeuille de produits nous permet de servir différents secteurs avec fiabilité, flexibilité et longue durée de vie.',
  },
  ar: {
    label: 'حول جاليوم فلاترز',
    heading: 'هندسة الترشيح للمستقبل',
    paragraph1:
      'تتصدّر جاليوم فلاترز تكنولوجيا الترشيح منذ أكثر من عقدين، من خلال تقديم حلول متقدمة لأنظمة الهواء والزيت والوقود والمياه والأنظمة الهيدروليكية عبر مختلف الصناعات حول العالم.',
    paragraph2:
      'ومن خلال البحث المستمر والهندسة الدقيقة والرقابة الصارمة على الجودة، بنينا سمعة قوية لأنظمة ترشيح تتجاوز معايير الصناعة. وتتيح لنا محفظة منتجاتنا الواسعة خدمة قطاعات متنوعة بموثوقية ومرونة وعمر خدمة طويل.',
  },
}

interface AboutGalliumSectionProps {
  locale: AppLocale
}

export function AboutGalliumSection({ locale }: AboutGalliumSectionProps) {
  const t = ABOUT_GALLIUM_TEXT[locale]
  const progressItems = PROGRESS_ITEMS_BY_LOCALE[locale]
  const imageBlock = (
    <div className="relative h-full w-full min-h-0 min-w-0 overflow-hidden rounded-sm bg-neutral-200 shadow-sm">
      <Image
        src="/about/about-title.jpg"
        alt="Industrial facility – filtration and engineering"
        fill
        className="object-cover object-center"
        sizes="(max-width: 768px) 100vw, 50vw"
      />
    </div>
  )

  return (
    <section className="bg-white pt-8 pb-12 md:pt-12 md:pb-20">
      <div className={`mx-auto max-w-4xl ${contentPadding}`}>
        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[1fr_1fr] lg:gap-12 lg:items-stretch">
          {/* Left column: label → heading → image (mobile/tablet) → body → progress bars */}
          <div className="min-w-0 flex flex-col items-start gap-6 text-left rtl:text-right order-1 lg:gap-8">
            <header className="space-y-1">
              <p className="font-poppins text-sm font-semibold uppercase tracking-wider text-[#FF0000] text-left rtl:text-right">
                {t.label}
              </p>
              <h2 className="font-bebas-neue text-2xl font-normal uppercase leading-tight text-[#FF0000] sm:text-3xl lg:text-4xl text-left rtl:text-right">
                {t.heading}
              </h2>
            </header>
            {/* Image in left column on mobile and tablet only; lg shows image in right column */}
            <div className="w-full max-w-[360px] md:max-w-[420px] lg:hidden">
              <div className="relative w-full overflow-hidden rounded-sm bg-neutral-200 shadow-sm aspect-[539/636]">
                <Image
                  src="/about/about-title.jpg"
                  alt="Industrial facility – filtration and engineering"
                  fill
                  className="object-cover"
                  sizes="(max-width: 767px) 100vw, (min-width: 768px) and (max-width: 1023px) 420px, 360px"
                />
              </div>
            </div>
            <div className="w-full min-w-0 space-y-4 text-left rtl:text-right">
              <p className="font-poppins text-sm font-light leading-[170%] text-neutral-700">
                {t.paragraph1}
              </p>
              <p className="font-poppins text-sm font-light leading-[170%] text-neutral-700">
                {t.paragraph2}
              </p>
            </div>
            <AnimatedProgressBars
              className="font-poppins w-full space-y-4 text-left rtl:text-right"
              items={progressItems}
            />
          </div>
          {/* Right column: image fills column on large screens */}
          <div className="hidden lg:block min-w-0 order-2 w-full h-full min-h-0">{imageBlock}</div>
        </div>
      </div>
    </section>
  )
}
